import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ButtonProps } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import { Valve } from '../types'
import { updateValve } from '../services/valve'

export interface ValveDetachInRuuviTagButtonProps
  extends Omit<ButtonProps, 'children' | 'onClick'> {
  valve: Valve
  buttonText?: string
  beforeSubmit?: () => void
  afterSubmit?: (valve: Valve) => void
}

export default function ValveDetachInRuuviTagButton({
  valve,
  buttonText,
  beforeSubmit,
  afterSubmit,
  ...props
}: ValveDetachInRuuviTagButtonProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation('valve')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The submitting state.
   */
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  /**
   * Handle the "submit" action.
   */
  async function handleSubmit() {
    try {
      if (valve?.inRuuviTagId) {
        setSubmitting(true)
        beforeSubmit?.()

        const updatedValve = await updateValve(valve.name, { inRuuviTag: null })

        dispatch(createSuccessNotification(t('detachInRuuviTagSuccess')))
        afterSubmit?.(updatedValve)
      }
    } catch (_) {
      dispatch(createErrorNotification(t('detachInRuuviTagError')))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {valve?.inRuuviTagId && (
        <LoadingButton
          variant="contained"
          color="warning"
          loading={isSubmitting}
          {...props}
          onClick={handleSubmit}
        >
          {buttonText ?? t('detachInRuuviTag')}
        </LoadingButton>
      )}
    </>
  )
}
